import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, OnDestroy, OnInit, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { QueryStringFilterService } from 'thkee-common';
import { Subject, Subscription, debounceTime, distinctUntilChanged } from 'rxjs';
import { AdminSessionFilterParams } from '@shared/services/live-sessions';
import { DateRangePresetsComponent } from './date-range-presets.component';

@Component({
  selector: 'app-session-filters',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [QueryStringFilterService.forComponent<AdminSessionFilterParams>()],
  template: `
    <!-- Always visible: Search + Status + Live Only -->
    <div class="flex flex-wrap items-center gap-3 mb-4">
      <div class="input-group flex-1 min-w-[220px] max-w-md">
        <div class="group">
          <svg xmlns="http://www.w3.org/2000/svg" class="input-icon w-4 h-4 text-neutral-400" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clip-rule="evenodd" />
          </svg>
          <input
            [value]="filters.search || ''"
            (input)="onSearchInput($event)"
            placeholder="Search by course, instructor..."
            i18n-placeholder
          />
        </div>
      </div>
      <select
        [value]="filters.temporal_status || ''"
        (change)="onSelectChange('temporal_status', $event)"
        class="form-control w-auto min-w-[150px]"
      >
        <option value="" i18n>All Statuses</option>
        <option value="upcoming" i18n>Upcoming</option>
        <option value="in_progress" i18n>In Progress</option>
        <option value="completed" i18n>Completed</option>
        <option value="cancelled" i18n>Cancelled</option>
      </select>
      <label class="flex items-center gap-2 cursor-pointer">
        <input
          type="checkbox"
          [checked]="!!filters.is_video_active"
          (change)="onLiveOnlyChange($event)"
          class="form-checkbox"
        />
        <span class="text-sm text-neutral-700 whitespace-nowrap" i18n>Live Only</span>
      </label>
      <button (click)="toggleExpanded()" class="btn-neutral flex items-center gap-1.5 text-sm">
        <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" viewBox="0 0 20 20" fill="currentColor">
          <path fill-rule="evenodd" d="M3 3a1 1 0 011-1h12a1 1 0 011 1v3a1 1 0 01-.293.707L12 11.414V15a1 1 0 01-.293.707l-2 2A1 1 0 018 17v-5.586L3.293 6.707A1 1 0 013 6V3z" clip-rule="evenodd" />
        </svg>
        <span i18n>More Filters</span>
        @if (activeAdvancedCount() > 0) {
          <span class="inline-flex items-center justify-center w-5 h-5 text-xs font-medium text-white bg-blue-600 rounded-full">{{ activeAdvancedCount() }}</span>
        }
        <svg xmlns="http://www.w3.org/2000/svg" class="w-3.5 h-3.5 transition-transform" [class.rotate-180]="isExpanded()" viewBox="0 0 20 20" fill="currentColor">
          <path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd" />
        </svg>
      </button>
    </div>

    <!-- Collapsible Advanced Filters -->
    @if (isExpanded()) {
      <div class="mb-4 p-4 border border-neutral-200 rounded-lg bg-white space-y-3">
        <!-- Row 1: Series ID, Series Name, Course Name, Product ID -->
        <div class="grid grid-cols-1 md:grid-cols-4 gap-3">
          <input
            type="number"
            [value]="filters.series_id || ''"
            (change)="onSelectChange('series_id', $event)"
            placeholder="Series ID"
            i18n-placeholder
            class="form-control"
          />
          <input
            [value]="filters.series_name || ''"
            (input)="onTextInput('series_name', $event)"
            placeholder="Series Name"
            i18n-placeholder
            class="form-control"
          />
          <input
            [value]="filters.course_name || ''"
            (input)="onTextInput('course_name', $event)"
            placeholder="Course Name"
            i18n-placeholder
            class="form-control"
          />
          <input
            [value]="filters.product_id || ''"
            (change)="onSelectChange('product_id', $event)"
            placeholder="Product ID"
            i18n-placeholder
            class="form-control"
          />
        </div>

        <!-- Row 2: Instructor Name, Email, ID -->
        <div class="grid grid-cols-1 md:grid-cols-3 gap-3">
          <input
            [value]="filters.instructor_name || ''"
            (input)="onTextInput('instructor_name', $event)"
            placeholder="Instructor Name"
            i18n-placeholder
            class="form-control"
          />
          <input
            [value]="filters.instructor_email || ''"
            (input)="onTextInput('instructor_email', $event)"
            placeholder="Instructor Email"
            i18n-placeholder
            class="form-control"
          />
          <input
            type="number"
            [value]="filters.instructor_id || ''"
            (change)="onSelectChange('instructor_id', $event)"
            placeholder="Instructor ID"
            i18n-placeholder
            class="form-control"
          />
        </div>

        <!-- Row 3: Participant Name, Email -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
          <input
            [value]="filters.participant_name || ''"
            (input)="onTextInput('participant_name', $event)"
            placeholder="Participant Name"
            i18n-placeholder
            class="form-control"
          />
          <input
            [value]="filters.participant_email || ''"
            (input)="onTextInput('participant_email', $event)"
            placeholder="Participant Email"
            i18n-placeholder
            class="form-control"
          />
        </div>

        <!-- Row 4: Date Range Presets -->
        <app-date-range-presets
          [dateFrom]="filters.date_from || ''"
          [dateTo]="filters.date_to || ''"
          (dateRangeChanged)="onDateRangeChange($event)"
        ></app-date-range-presets>

        <!-- Clear button -->
        <div class="flex items-center pt-1">
          <button (click)="clearFilters()" class="btn-neutral text-sm" i18n>Clear All Filters</button>
        </div>
      </div>
    }
  `,
  imports: [FormsModule, DateRangePresetsComponent],
})
export class SessionFiltersComponent implements OnInit, OnDestroy {
  private readonly filterService = inject(QueryStringFilterService<AdminSessionFilterParams>);
  private readonly cdr = inject(ChangeDetectorRef);

  readonly filtersChanged = output<AdminSessionFilterParams>();

  isExpanded = signal(false);
  activeAdvancedCount = signal(0);
  filters: AdminSessionFilterParams = {};

  private searchSubject = new Subject<string>();
  private textSubjects = new Map<string, Subject<string>>();
  private subscriptions = new Subscription();

  private readonly advancedFields: (keyof AdminSessionFilterParams)[] = [
    'series_id', 'series_name', 'course_name', 'product_id',
    'instructor_name', 'instructor_email', 'instructor_id',
    'participant_name', 'participant_email',
    'date_from', 'date_to',
  ];

  ngOnInit(): void {
    this.subscriptions.add(
      this.filterService.initialValue.subscribe(params => {
        this.filters = this.coerceTypes({ ...params });
        this.updateAdvancedCount();
        if (this.activeAdvancedCount() > 0) {
          this.isExpanded.set(true);
        }
        this.cdr.markForCheck();
        // Emit to parent so it loads data with URL-restored filters
        this.filtersChanged.emit(this.filters);
      })
    );

    this.subscriptions.add(
      this.searchSubject.pipe(
        debounceTime(300),
        distinctUntilChanged(),
      ).subscribe(value => {
        this.filters.search = value || undefined;
        this.applyFilters();
        this.cdr.markForCheck();
      })
    );
  }

  toggleExpanded(): void {
    this.isExpanded.update(v => !v);
  }

  onSearchInput(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.searchSubject.next(value);
  }

  /** Instant apply for dropdowns, checkboxes, numeric fields */
  onSelectChange(field: keyof AdminSessionFilterParams, event: Event): void {
    const value = (event.target as HTMLInputElement | HTMLSelectElement).value;
    (this.filters as Record<string, unknown>)[field] = value || undefined;
    this.updateAdvancedCount();
    this.applyFilters();
  }

  /** Debounced apply for text inputs in advanced filters */
  onTextInput(field: keyof AdminSessionFilterParams, event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    (this.filters as Record<string, unknown>)[field] = value || undefined;

    if (!this.textSubjects.has(field)) {
      const subject = new Subject<string>();
      this.textSubjects.set(field, subject);
      this.subscriptions.add(
        subject.pipe(debounceTime(400), distinctUntilChanged()).subscribe(() => {
          this.updateAdvancedCount();
          this.applyFilters();
          this.cdr.markForCheck();
        })
      );
    }
    this.textSubjects.get(field)!.next(value);
  }

  onLiveOnlyChange(event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    this.filters.is_video_active = checked || undefined;
    this.applyFilters();
  }

  onDateRangeChange(range: { date_from: string; date_to: string }): void {
    this.filters.date_from = range.date_from || undefined;
    this.filters.date_to = range.date_to || undefined;
    this.updateAdvancedCount();
    this.applyFilters();
  }

  applyFilters(): void {
    const cleanFilters = { ...this.filters, page: 1 };
    this.filterService.patch(cleanFilters);
    this.filtersChanged.emit(cleanFilters);
  }

  clearFilters(): void {
    this.filters = {};
    // Use patch with all filter keys explicitly undefined to preserve non-filter URL params (tab, etc.)
    const resetParams: Record<string, undefined> = { page: undefined, page_size: undefined };
    for (const field of this.advancedFields) {
      resetParams[field] = undefined;
    }
    resetParams['search'] = undefined;
    resetParams['temporal_status'] = undefined;
    resetParams['is_video_active'] = undefined;
    resetParams['ordering'] = undefined;
    this.filterService.patch(resetParams as unknown as AdminSessionFilterParams);
    this.updateAdvancedCount();
    this.filtersChanged.emit({});
  }

  /**
   * Coerce URL string params back to correct types.
   * URL params are always strings; we need booleans for is_video_active.
   */
  private coerceTypes(params: AdminSessionFilterParams): AdminSessionFilterParams {
    if (params.is_video_active !== undefined) {
      // URL param comes as string "true"/"false"
      params.is_video_active = (params.is_video_active as unknown) === 'true' || params.is_video_active === true
        ? true
        : undefined;
    }
    return params;
  }

  private updateAdvancedCount(): void {
    const count = this.advancedFields.filter(f => {
      const val = this.filters[f];
      return val !== undefined && val !== '' && val !== null;
    }).length;
    this.activeAdvancedCount.set(count);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
